import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  DrawerContext,
  ModalContext,
  ModalModel,
} from '@notino/react-styleguide';

import { isDesktop } from '@utils/helpers';

import { Content } from './components/Content';
import { Gallery } from './components/Gallery';
import { reviewPhotoModalMessages } from './messages';
import {
  createReviewPhotoModalStore,
  Image,
  ReviewPhotoModalProvider,
} from './store';
import { ModalContent, Wrapper } from './styled';

type Props = {
  reviewId: number;
  initialImage: Image;
  showNextReviewArrow?: boolean;
  showPrevReviewArrow?: boolean;
  onNextReviewClick?: () => void;
  onPrevReviewClick?: () => void;
};

export const useReviewPhotoModal = () => {
  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const { openDrawer, closeDrawer } = DrawerContext.useDrawerContext();

  const openReviewPhotoModal = (props: Props) => {
    if (isDesktop()) {
      toggleModal(<ReviewPhotoModal {...props} />, {
        size: ModalModel.ModalSizes.s1100,
        noBorders: true,
        withFocusTrap: true,
      });
    } else {
      openDrawer(<ReviewPhotoModal {...props} />, {
        title: (
          <>
            <div style={{ width: 52 }} />
            <FormattedMessage {...reviewPhotoModalMessages.drawerTitle} />
          </>
        ),
        initialPosition: 'top',
        hideScrollbar: true,
      });
    }
  };

  return {
    openReviewPhotoModal,
    closeReviewPhotoModal: () => {
      hideModal();
      closeDrawer();
    },
  };
};

export const ReviewPhotoModal = ({
  reviewId,
  initialImage,
  ...rest
}: Props) => {
  const store = React.useMemo(
    () => createReviewPhotoModalStore({ reviewId, initialImage }),
    [reviewId, initialImage]
  );

  return (
    <ReviewPhotoModalProvider value={store}>
      <Wrapper>
        <ModalContent>
          <Gallery {...rest} />
          <Content />
        </ModalContent>
      </Wrapper>
    </ReviewPhotoModalProvider>
  );
};
